import { motion } from 'framer-motion';
import { ChevronLeft, Music2, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const SurahCard = ({ surah, onClick, onEdit, onDelete, index, showActions = true }) => {
  const { t } = useLanguage();

  const handleCardClick = (e) => {
    if (e.target.closest('[data-menu-area]')) return;
    onClick();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="group relative"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        className="relative overflow-hidden rounded-3xl p-6 cursor-pointer w-full bg-card border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300 text-right"
        dir="rtl"
        data-testid={`surah-card-${surah.id}`}
      >
        {/* Background Number - Left side for RTL */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-8xl font-heading font-bold text-muted/20 pointer-events-none select-none">
          {surah.number}
        </div>

        {/* Content */}
        <div className="relative z-10 flex items-center gap-4">
          {/* Surah Number Badge */}
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <span className="font-heading font-semibold text-primary text-lg">
              {surah.number}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-arabic text-2xl text-foreground mb-1 truncate" dir="rtl">
              {surah.name_arabic}
            </h3>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Music2 className="w-3 h-3" />
              <span>
                {surah.excerpt_count || 0} {(surah.excerpt_count >= 2 && surah.excerpt_count <= 10) ? 'صفحات' : 'صفحة'}
              </span>
            </div>
          </div>

          {/* Arrow - Left chevron for RTL */}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>

        {/* Actions Menu */}
        {showActions && onEdit && onDelete && (
          <div 
            data-menu-area
            className="absolute top-3 left-3 z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted"
                  data-testid={`surah-menu-${surah.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem 
                  onClick={onEdit}
                  data-testid={`surah-edit-${surah.id}`}
                >
                  <Pencil className="w-4 h-4 ml-2" />
                  {t('editSurah')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                  data-testid={`surah-delete-${surah.id}`}
                >
                  <Trash2 className="w-4 h-4 ml-2" />
                  {t('deleteSurah')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </motion.div>
  );
};
